import React from 'react';
import PropTypes from 'prop-types';

import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutline';

import Button from 'ui/Button';
import Label from 'ui/text/Label';

import NumberTextField from 'components/NumberTextfield';

// SELL06 — options menu + sparse SKU grid for merch products. Options declare
// the axes and value ordering; each variant row is one stocked tuple with
// its own supply. The grid is sparse: only rows the org adds are sold. The API
// blocks deleting sold SKUs (they are discontinued instead) and, per SELL18,
// lets axes be added but never renamed or removed once variants exist.
//
// Both halves are laid out as ledgers rather than stacks of form fields: the
// column names live once in a header row, so a row carries values only. A cell
// that the API will not let the org change is rendered as plain text, so the
// controls that remain are the ones that actually do something.
const VariantsEditor = ({ object, onObjectChange }) => {
  const options = object.options || [];
  const variants = object.variants || [];

  const update = (changes) => onObjectChange({ ...object, ...changes });
  const cleanValues = (axis) => (axis.values || []).map((value) => value.trim()).filter(Boolean);

  // An axis that saved variants already key on is frozen: renaming it would
  // desync stored tuples from purchase history and removing it would merge
  // distinct SKUs, so the API rejects both. An axis added in this session is
  // free — `unsaved` marks it, rather than inferring from the variants' own
  // tuples, which we mutate as the org fills the new axis in. Clearing every
  // variant row unlocks everything, matching the builder's teardown path.
  const hasSavedVariants = variants.some((variant) => variant.id);
  const axisLocked = (axis) => hasSavedVariants && !axis?.unsaved;

  // Only fully declared axes form the grid, so a half-typed new axis never
  // blanks out the variant rows that already exist.
  const readyAxes = options.filter((axis) => axis.name.trim() && cleanValues(axis).length > 0);

  const handleAxisNameChange = (index, name) => {
    const next = options.map((axis, i) => (i === index ? { ...axis, name } : axis));
    update({ options: next });
  };

  // Withdrawing a value a saved variant keys on is blocked, but at save time
  // (handleValidation) rather than per keystroke: rejecting a change reverts this
  // controlled input while the caret has already moved on, so the following
  // keystrokes land in the wrong place and quietly corrupt the list. Free typing
  // keeps reordering and mid-string edits usable.
  const handleAxisValuesChange = (index, raw) => {
    const values = raw.split(',').map((value) => value.trimStart());
    const next = options.map((item, i) => (i === index ? { ...item, values } : item));
    update({ options: next });
  };

  const addAxis = (event) => {
    event.preventDefault(); // ui/Button renders a bare <button>, which would submit the wizard
    update({ options: [...options, { name: '', values: [], unsaved: true }] });
  };

  const removeAxis = (index) => {
    if (axisLocked(options[index])) return;
    update({ options: options.filter((_, i) => i !== index) });
  };

  // Every tuple the declared options allow, in display order (S/blue, S/black, …).
  const allTuples = () => readyAxes.reduce((tuples, axis) => (
    tuples.flatMap((tuple) => cleanValues(axis)
      .map((value) => ({ ...tuple, [axis.name.trim()]: value })))
  ), [{}]);

  const tupleKey = (tuple) => readyAxes.map((axis) => tuple[axis.name.trim()] || '').join('||');
  const stockedKeys = new Set(variants.map((variant) => tupleKey(variant.options || {})));

  // Seed each new row with the first tuple not stocked yet, so repeated clicks
  // walk the grid instead of piling up duplicates the org has to fix at save.
  const nextTuple = readyAxes.length > 0 &&
    allTuples().find((tuple) => !stockedKeys.has(tupleKey(tuple)));

  const addVariant = (event) => {
    event.preventDefault();
    if (!nextTuple) return;

    update({ variants: [...variants, { options: nextTuple, supply_limit: '', active: true }] });
  };

  const changeVariant = (index, changes) => {
    const next = variants.map((variant, i) => (i === index ? { ...variant, ...changes } : variant));
    update({ variants: next });
  };

  const removeVariant = (index) => {
    update({ variants: variants.filter((_, i) => i !== index) });
  };

  // A sold SKU's existing axes are settled, but an axis added afterwards is being
  // assigned for the first time — the org picks how already-delivered units get
  // labelled rather than inheriting whichever value happens to be first (SELL18).
  const axisFrozenFor = (variant, axis) =>
    !!variant.id && variant.issued_counter > 0 && !axis?.unsaved;

  // An empty stock field means unlimited, so the Left column reads it from the
  // field the org is editing rather than from the saved figure. The count itself
  // has to come from the server: it nets off stock held by event reservations
  // (SELL16), which this form knows nothing about.
  const isUnlimited = (variant) => variant.supply_limit === '' || variant.supply_limit == null;
  const soldOut = (variant) => !isUnlimited(variant) && (variant.available_supply ?? 0) === 0;
  const leftLabel = (variant) => (isUnlimited(variant) ? '∞' : `${variant.available_supply ?? 0}`);

  return (
    <div className="variants-editor">
      <Label secondary>
        List the choices buyers make, then stock each combination you sell.
        Stock is the total you made — leave it empty for unlimited.
      </Label>

      <div className="variants-editor_options">
        <div className="variants-editor_grid" role="table" aria-label="Options">
          {/* Nothing declared yet: column names with no rows under them would be
              the loudest thing on an empty section. */}
          {options.length > 0 && (
            <div className="variants-editor_head" role="row">
              <span role="columnheader">Option</span>
              <span role="columnheader">Values</span>
              <span role="columnheader" />
              <span role="columnheader" />
            </div>
          )}

          {options.map((axis, index) => (
            <div className="variant-row" role="row" key={index}>
              <span role="cell">
                {axisLocked(axis) ? (
                  <span
                    className="variant-value"
                    title="Variants already use this option, so its name can’t change">
                    {axis.name}
                  </span>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    className="variant-field"
                    placeholder="e.g. Size"
                    inputProps={{ 'aria-label': 'Option name' }}
                    value={axis.name}
                    onChange={(event) => handleAxisNameChange(index, event.target.value)}
                  />
                )}
              </span>

              <span role="cell">
                <TextField
                  fullWidth
                  size="small"
                  className="variant-field"
                  placeholder="e.g. S, M, L"
                  inputProps={{ 'aria-label': `Values for ${axis.name || 'this option'}` }}
                  value={(axis.values || []).join(',')}
                  onChange={(event) => handleAxisValuesChange(index, event.target.value)}
                />
              </span>

              <span role="cell" />

              <span className="variant-row_action" role="cell">
                {/* A locked axis can never be removed, so the control goes away
                    rather than sitting there permanently disabled. */}
                {!axisLocked(axis) && (
                  <IconButton
                    size="small"
                    aria-label="Remove option"
                    onClick={() => removeAxis(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </span>
            </div>
          ))}
        </div>

        <Button text small icon={AddCircleOutlineOutlinedIcon} onClick={addAxis}>
          Add option
        </Button>
      </div>

      {readyAxes.length > 0 && (
        <div
          // The tinted surface is the ledger's; with nothing stocked yet there is
          // no ledger, just the invitation to start one.
          className={`variants-editor_stock${variants.length > 0 ? ' variants-editor_stock-filled' : ''}`}
          style={{ '--variant-columns': readyAxes.length }}>
          <div className="variants-editor_grid" role="table" aria-label="Stock per combination">
            {variants.length > 0 && (
              <div className="variants-editor_head" role="row">
                {readyAxes.map((axis) => (
                  <span role="columnheader" key={axis.name} title={axis.name}>{axis.name}</span>
                ))}
                <span role="columnheader" />
                <span role="columnheader" className="variant-number">Stock</span>
                <span role="columnheader" className="variant-number">Sold</span>
                <span role="columnheader" className="variant-number">Left</span>
                <span role="columnheader" className="variant-centred">On sale</span>
                <span role="columnheader" />
              </div>
            )}

            {variants.map((variant, index) => (
              <div className="variant-row" role="row" key={variant.id || `new-${index}`}>
                {readyAxes.map((axis) => (
                  <span role="cell" key={axis.name}>
                    {axisFrozenFor(variant, axis) ? (
                      <span
                        className="variant-value"
                        title={`Units already sold, so ${axis.name} can’t change`}>
                        {variant.options?.[axis.name.trim()]}
                      </span>
                    ) : (
                      <FormControl fullWidth size="small" className="variant-field">
                        <Select
                          MenuProps={{ className: 'variant-option-menu' }}
                          inputProps={{ 'aria-label': axis.name }}
                          value={variant.options?.[axis.name.trim()] || ''}
                          onChange={(event) => changeVariant(index, {
                            options: { ...variant.options, [axis.name.trim()]: event.target.value },
                          })}
                        >
                          {cleanValues(axis).map((value) => (
                            <MenuItem value={value} key={value}>{value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </span>
                ))}

                <span role="cell" />

                <span role="cell">
                  <NumberTextField
                    fullWidth
                    size="small"
                    className="variant-field variant-field_number"
                    name={`variant-supply-${index}`}
                    placeholder="∞"
                    inputProps={{ 'aria-label': 'Stock' }}
                    value={variant.supply_limit ?? ''}
                    onChange={(event) => changeVariant(index, { supply_limit: event.target.value })}
                  />
                </span>

                {/* Sold and Left only exist once a row has been saved and can
                    have taken orders. */}
                <span className="variant-value variant-number variant-value_quiet" role="cell">
                  {variant.id ? variant.issued_counter : ''}
                </span>

                <span
                  className={`variant-value variant-number${soldOut(variant) ? '' : ' variant-value_quiet'}`}
                  role="cell">
                  {variant.id ? leftLabel(variant) : ''}
                </span>

                <span className="variant-row_switch" role="cell">
                  {/* Any saved SKU can be taken off sale — a colour out at the
                      printer or a size listed by mistake needs pausing whether
                      or not it has sold, and pausing keeps its stock figure and
                      event allocations that deleting the row would discard. */}
                  {variant.id && (
                    <Switch
                      size="small"
                      color="warning"
                      inputProps={{ 'aria-label': 'On sale' }}
                      checked={variant.active !== false}
                      onChange={(event) => changeVariant(index, { active: event.target.checked })}
                    />
                  )}
                </span>

                {/* A sold SKU is discontinued with the switch instead of deleted —
                    its purchase history has to survive. */}
                <span className="variant-row_action" role="cell">
                  {!(variant.id && variant.issued_counter > 0) && (
                    <IconButton
                      size="small"
                      aria-label="Remove variant"
                      onClick={() => removeVariant(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </span>
              </div>
            ))}
          </div>

          <Button
            text
            small
            icon={AddCircleOutlineOutlinedIcon}
            disabled={!nextTuple}
            onClick={addVariant}>
            Add variant
          </Button>
        </div>
      )}
    </div>
  );
};

VariantsEditor.propTypes = {
  object: PropTypes.object.isRequired,
  onObjectChange: PropTypes.func.isRequired,
};

export default VariantsEditor;
